/**
 * API / MCP / ダッシュボードで共有するドメイン型。
 * D1 のテーブル定義 (packages/db/migrations) と 1:1 で対応させる。
 */

/** 'YYYY-MM' 形式の対象年月 */
export type MonthKey = string;
/** 'YYYY-MM-DD' 形式の作業日 */
export type DateKey = string;
/** Unix epoch 秒 (UTC) */
export type UnixSeconds = number;
/** 金額 (円)。単価に小数は使わない。 */
export type Yen = number;

/**
 * ロール。`mcp < member < admin` の階層で、上位は下位を含む。
 *   mcp    : MCP と、自分の工数・稼働だけ (LP の MCP Connect プラン)
 *   member : 管理画面フル (LP の Full Management プラン)
 *   admin  : 管理画面フル + マスタ管理
 * 判定関数は roles.ts。
 */
export type UserRole = 'mcp' | 'member' | 'admin';
export type UserStatus = 'active' | 'invited' | 'disabled';
/**
 * テナントの状態。
 *   active           : 通常
 *   suspended        : 運営が強制停止。ログインの時点で 403、既存セッションは未ログイン扱い
 *   canceled         : 解約済み。読み取りだけ通す (エクスポートに到達させるため)
 *   pending_deletion : 削除予定。canceled と同じ扱いで、削除予定日を持つ
 */
export type TenantStatus = 'active' | 'suspended' | 'canceled' | 'pending_deletion';
export const TENANT_STATUSES = [
  'active',
  'suspended',
  'canceled',
  'pending_deletion',
] as const satisfies readonly TenantStatus[];

export function isTenantStatus(value: string): value is TenantStatus {
  return (TENANT_STATUSES as readonly string[]).includes(value);
}

/**
 * 読み取りだけを通す状態か。トライアル切れと同じ扱いで、更新系と `/mcp` を
 * 説明つきの 403 で止める。
 *
 * **`suspended` と同じ扱いにしてはいけない。** あちらはログインごと塞ぐので、
 * 解約した管理者が自分のデータを持ち出せなくなる。
 */
export function isReadOnlyTenantStatus(status: TenantStatus): boolean {
  return status === 'canceled' || status === 'pending_deletion';
}

export type ProjectStatus = 'active' | 'archived';
export type TimeLogSource = 'mcp' | 'web';

export type ProjectTypeStatus = 'active' | 'archived';

/**
 * プロジェクト種別。テナントが管理するマスタ。
 *
 * 単なるラベルではなく、会計上の意味を 2 つ持つ。この 2 つが無いと、
 * 新しく作った種別の工数が請求可能なのか直接工数なのかを誰も決められない。
 * 資産化の可否は種別ではなくプロジェクト単位 (`Project.isCapitalizable`)。
 */
export interface ProjectType {
  id: string;
  tenantId: string;
  name: string;
  /** この種別のプロジェクトに記録した工数の `isBillable` 既定値 */
  defaultBillable: boolean;
  /** 稼働レポートで development を直接工数として数えるか */
  countsAsDirect: boolean;
  status: ProjectTypeStatus;
  createdAt: UnixSeconds;
  updatedAt: UnixSeconds;
}

/**
 * 予算の時間軸。プロジェクトごとに選ぶ。
 *   none    : 予算なし
 *   total   : 通期 1 本 (受託。契約総額が先に決まり、月に割れない)
 *   monthly : 'YYYY-MM' ごとに 1 本 (自社プロダクトの年度予算)
 *
 * 1 プロジェクトが通期と月次を同時に持つと「月次の合計 ≠ 通期」の整合を
 * 守り続けることになるので、一方だけを持つ。
 */
export type ProjectBudgetMode = 'none' | 'total' | 'monthly';

export const PROJECT_BUDGET_MODES = ['none', 'total', 'monthly'] as const;

export function isProjectBudgetMode(value: string): value is ProjectBudgetMode {
  return (PROJECT_BUDGET_MODES as readonly string[]).includes(value);
}

/**
 * プロジェクトの予算。金額 (円) だけを持つ。
 *
 * 工数の予算は作らない。`assignments.planned_hours` がプロジェクト × ユーザー ×
 * 月の予定工数を既に持っており、二重管理になるため。
 */
export interface ProjectBudget {
  /** 通期は null、月次は 'YYYY-MM' */
  month: MonthKey | null;
  /** 原価予算。突き合わせるのは `monthly_summary.cost_amount` */
  costBudget: Yen | null;
  /** 受注金額。突き合わせるのは `monthly_summary.revenue_amount` (想定売上) */
  revenueBudget: Yen | null;
  note: string | null;
}

/**
 * 作業カテゴリ。MCP 側が会話内容から自動判定して埋める。
 *   development : 新規開発
 *   maintenance : バグ修正・保守運用
 *   meeting     : 会議・調整
 *   other       : その他
 */
export type TimeLogCategory = 'development' | 'maintenance' | 'meeting' | 'other';

/**
 * 二段階認証を要求する範囲。テナントが決める。
 *   off   : 要求しない (既定)
 *   admin : ロール admin にだけ要求する
 *   all   : mcp を含む全ロールに要求する
 */
export type MfaPolicy = 'off' | 'admin' | 'all';

export interface Tenant {
  id: string;
  name: string;
  status: TenantStatus;
  /** IANA タイムゾーン名。MCP が「今日」を決めるのに使う。 */
  timezone: string;
  /** テナント既定の原価単価 (円/時) */
  defaultHourlyCost: Yen;
  /** テナント既定の売上 (チャージ) 単価 (円/時) */
  defaultHourlyRate: Yen;
  /** フル枠: 購入済み (Stripe と同期) */
  purchasedFullSeats: number;
  /** フル枠: 運営が手動で付与 */
  grantedFullSeats: number;
  /** MCP 枠: 購入済み (Stripe と同期) */
  purchasedMcpSeats: number;
  /** MCP 枠: 運営が手動で付与 */
  grantedMcpSeats: number;
  /**
   * トライアルの終了時刻 (Unix 秒)。null はトライアルではない。
   * 期限を過ぎると読み取りだけになる (更新系と MCP は止まる)。
   */
  trialEndsAt: UnixSeconds | null;
  /** 削除予定日 (Unix 秒)。`pending_deletion` のときだけ入る。 */
  deletionScheduledAt: UnixSeconds | null;
  /** テナントごとの機能開放フラグ */
  featureFlags: Record<string, boolean>;
  /** MCP の tool description に連結する、テナント全体の記録ルール */
  mcpCustomInstruction: string | null;
  /** 二段階認証を要求する範囲。既定は 'off'。 */
  mfaPolicy: MfaPolicy;
  createdAt: UnixSeconds;
  updatedAt: UnixSeconds;
}

type SeatColumns = Pick<
  Tenant,
  'purchasedFullSeats' | 'grantedFullSeats' | 'purchasedMcpSeats' | 'grantedMcpSeats'
>;

/**
 * 種別ごとの利用可能なライセンス枠。招待時にその種別の利用者数と突き合わせる。
 * (License は roles.ts。循環参照を避けるためここでは文字列で受ける)
 */
export function availableSeats(tenant: SeatColumns, license: 'mcp' | 'full'): number {
  return license === 'mcp'
    ? tenant.purchasedMcpSeats + tenant.grantedMcpSeats
    : tenant.purchasedFullSeats + tenant.grantedFullSeats;
}

export interface User {
  id: string;
  tenantId: string;
  email: string;
  name: string;
  role: UserRole;
  status: UserStatus;
  /** 個人の原価単価。null ならテナント既定値を使う。 */
  hourlyCost: Yen | null;
  /** 個人の売上単価。null ならテナント既定値を使う。 */
  hourlyRate: Yen | null;
  createdAt: UnixSeconds;
  updatedAt: UnixSeconds;
}

export interface Project {
  id: string;
  tenantId: string;
  name: string;
  status: ProjectStatus;
  /** 種別。未設定 (null) は請求可能・直接工数として扱う。 */
  projectTypeId: string | null;
  /** ソフトウェア資産として計上しうるプロジェクトか */
  isCapitalizable: boolean;
  /** このプロジェクトに限った、AI への追加の記録ルール */
  mcpCustomInstruction: string | null;
  createdAt: UnixSeconds;
  updatedAt: UnixSeconds;
}

/** アサイン = 月次の「予定工数」 */
export interface Assignment {
  id: string;
  tenantId: string;
  projectId: string;
  userId: string;
  month: MonthKey;
  plannedHours: number;
  /** このプロジェクトに限った特別原価単価。null なら個人/テナントの単価を使う。 */
  customHourlyCost: Yen | null;
  note: string | null;
  createdAt: UnixSeconds;
  updatedAt: UnixSeconds;
}

/** 工数ログ = 「実績工数」。主に MCP 経由で記録される。 */
export interface TimeLog {
  id: string;
  tenantId: string;
  userId: string;
  projectId: string;
  hours: number;
  workedOn: DateKey;
  category: TimeLogCategory;
  /** この工数が資産化対象か (projects.isCapitalizable と category から判定) */
  isCapitalizedLog: boolean;
  /** 請求可能工数か */
  isBillable: boolean;
  note: string | null;
  source: TimeLogSource;
  createdAt: UnixSeconds;
  updatedAt: UnixSeconds;
}

/**
 * monthly_summary view の 1 行。
 * 予定と実績の突き合わせに加えて、原価・想定売上・資産化区分まで含む。
 */
export interface MonthlySummaryRow {
  tenantId: string;
  month: MonthKey;
  projectId: string;
  userId: string;
  projectTypeId: string | null;
  projectTypeName: string | null;
  /** 種別未設定なら true (旧 client_work と同じ扱い) */
  countsAsDirect: boolean;
  isCapitalizable: boolean;

  plannedHours: number;
  actualHours: number;
  /** actualHours - plannedHours。正なら超過、負なら未消化。 */
  diffHours: number;
  capitalizedHours: number;
  expensedHours: number;
  billableHours: number;
  developmentHours: number;
  maintenanceHours: number;
  meetingHours: number;
  otherHours: number;

  /** 実際に適用された単価 (円/時) */
  hourlyCost: Yen;
  hourlyRate: Yen;

  costAmount: Yen;
  capitalizedCost: Yen;
  expensedCost: Yen;
  revenueAmount: Yen;
  grossProfit: Yen;
}

/** 全テナント共通のお知らせ (運営が配信する) */
export interface Announcement {
  id: string;
  message: string;
  activeFrom: UnixSeconds;
  activeTo: UnixSeconds | null;
  createdAt: UnixSeconds;
  updatedAt: UnixSeconds;
}

/**
 * 想定売上・粗利を数字で出してよいかの状態。
 *
 *   ok           : 請求可能工数があり、その全部に売上単価が解決できた
 *   not_billable : 請求可能工数が無い (自社プロダクト等)。売上 0 は正しく、
 *                  粗利という指標がその行に存在しないだけ
 *   missing_rate : 請求可能工数の全部で売上単価が解決できない
 *   partial_rate : 一部でだけ解決できる。計算すると必ず過小になる
 *
 * `tenants.default_hourly_rate` は NOT NULL DEFAULT 0 なので、「単価が未設定」は
 * NULL ではなく値 0 として現れる。0 円/時を実在の単価として扱うと、請求可能な
 * 工数があるのに単価を入れていないテナントで 粗利 = -原価 の嘘の赤字が出る。
 *
 * 判定材料はこの 2 つだけで、プロジェクト種別は見ない。種別はテナントが消せる
 * マスタなので、見ると種別をいじった瞬間に過去月の収支が動く。
 * `time_logs.is_billable` は記録時に焼かれているので動かない。
 */
export type ProjectRevenueState = 'ok' | 'not_billable' | 'missing_rate' | 'partial_rate';

/** 工数は REAL なので、比較には丸め誤差ぶんの許容を置く (0.001 時間 = 3.6 秒) */
const HOURS_EPSILON = 0.001;

export function resolveRevenueState(
  billableHours: number,
  unratedBillableHours: number,
): ProjectRevenueState {
  if (billableHours <= HOURS_EPSILON) return 'not_billable';
  if (unratedBillableHours <= HOURS_EPSILON) return 'ok';
  if (unratedBillableHours >= billableHours - HOURS_EPSILON) return 'missing_rate';
  return 'partial_rate';
}
